import { useMemo, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import ActorCard from '@/components/base/ActorCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { actorList, actorRegions, actorGenders, actorPeriods } from '@/mocks/actors';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

function matchPeriod(activeYears: string[], period: string) {
  if (period === '全部') return true;
  const startYear = Number(activeYears[0].slice(0, 4));
  const span = 2026 - startYear;
  if (period === '近三年活跃') return span <= 3;
  if (period === '五年以上') return span >= 5;
  if (period === '十年以上') return span >= 10;
  return true;
}

export default function ActorsCategory() {
  usePageMeta(
    '漂亮的姐姐 - 演员动态 - 明星近况与作品资讯',
    '漂亮的姐姐演员动态分类页，汇总热门演员的最新作品、访谈与近况资讯，支持按性别、地区与活跃时间段筛选，随时掌握明星动态。',
  );

  const [gender, setGender] = useState('全部');
  const [region, setRegion] = useState('全部地区');
  const [period, setPeriod] = useState('全部');
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      actorList.filter((actor) => {
        const matchGender = gender === '全部' || actor.gender === gender;
        const matchRegion = region === '全部地区' || actor.region === region;
        const matchActive = matchPeriod(actor.activeYears, period);
        return matchGender && matchRegion && matchActive;
      }),
    [gender, region, period],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setGender('全部');
    setRegion('全部地区');
    setPeriod('全部');
    setPage(1);
  };

  const chip = (active: boolean) =>
    `px-3.5 py-1.5 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
      active ? 'bg-secondary-500 text-background-50 dark:text-foreground-950' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
    }`;

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员动态' }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">演员动态</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          关注喜爱的演员近况。这里汇总热门演员的最新作品、访谈与相关资讯，支持按性别、地区与活跃时间段筛选。
        </p>
      </header>

      <div className="mt-6 bg-background-50 border border-background-200 rounded-lg p-4 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-label text-foreground-500 w-14 shrink-0">性别</span>
          <div className="flex flex-wrap gap-2">
            {actorGenders.map((item) => (
              <button key={item} type="button" className={chip(gender === item)} onClick={() => { setGender(item); setPage(1); }}>
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-label text-foreground-500 w-14 shrink-0">地区</span>
          <div className="flex flex-wrap gap-2">
            {actorRegions.map((item) => (
              <button key={item} type="button" className={chip(region === item)} onClick={() => { setRegion(item); setPage(1); }}>
                {item.replace('全部地区', '全部')}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-label text-foreground-500 w-14 shrink-0">活跃期</span>
          <div className="flex flex-wrap gap-2">
            {actorPeriods.map((item) => (
              <button key={item} type="button" className={chip(period === item)} onClick={() => { setPeriod(item); setPage(1); }}>
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <strong className="text-secondary-700">{filtered.length}</strong> 位演员
        </p>
        <button type="button" onClick={reset} className="text-xs font-label text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
          重置筛选
        </button>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5">
            {pageItems.map((actor) => (
              <ActorCard key={actor.id} actor={actor} />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-4">
          <EmptyState
            title="暂无符合条件的演员"
            description="试试调整或重置筛选条件，浏览更多演员动态。"
            actionLabel="重置筛选条件"
            onAction={reset}
          />
        </div>
      )}
    </div>
  );
}